import React, { useState } from 'react';
import { FaLock, FaCheck } from 'react-icons/fa';
import UpgradeModal from './UpgradeModal';
import { useAuth } from '../context/AuthContext';

/**
 * Elegant guest-blocker shown when an unauthenticated/guest user lands on
 * a paid-only page. Designed to feel value-forward — NOT spammy.
 *
 * Default `requiredTier='light'` afspejler den nye abonnementsmodel:
 * ALLE nuværende betalte funktioner låses op af BÅDE Light og Pro.
 * Kun eksklusive fremtidige Pro-features bør sætte requiredTier='pro'.
 */
const ProFeatureLocked = ({
  title,
  description,
  bullets = [],
  ctaLabel = 'Se abonnementer',
  reason = 'general',
  requiredTier = 'light',
  testId = 'pro-feature-locked',
}) => {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { user } = useAuth();

  return (
    <div className="space-y-6 fade-in" data-testid={testId}>
      <div>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          {title}
          <span
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200"
            title="Kræver medlemskab"
          >
            <FaLock className="text-[10px]" /> Medlem
          </span>
        </h1>
        {description && <p className="text-gray-600">{description}</p>}
      </div>

      <div className="bg-gradient-to-br from-gray-50 to-cyan-50/40 border border-gray-200 rounded-2xl p-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
              <FaLock className="text-gray-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Funktionen kræver et abonnement
              </h2>
              <p className="text-sm text-gray-500">
                Adgang med både Light og Pro. Du kan gratis se hvad der er muligt — opgradér når du er klar.
              </p>
            </div>
          </div>

          {bullets.length > 0 && (
            <ul className="space-y-2 mb-6">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <FaCheck className="text-emerald-500 mt-1 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => setShowUpgrade(true)}
            data-testid={`${testId}-cta`}
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            {ctaLabel}
          </button>
          <p className="mt-3 text-xs text-gray-400">
            Ingen forpligtelser. Du kan altid prøve gratis først.
          </p>
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        reason={reason}
        currentUser={user}
        requiredTier={requiredTier}
      />
    </div>
  );
};

export default ProFeatureLocked;
